import ProductFilters from '@moncler/components/listing/ProductFilters';
import ProductTile from '@moncler/components/listing/ProductTile';
import { SearchActionTypes } from '@state/features/common.types';
import analytics from '@utils/analytics';
import api from '@utils/api';
import einsteinutils from '@utils/einsteinutils';
import { debounce } from '@utils/functions';
import useScrollDirection from '@utils/useScrollDirection';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import SearchNoResults from './SearchNoResults';
import SearchSuggested from './SearchSuggested';
import SearchSuggestionPhrases from './SearchSuggestionPhrases';
import useInnerWidth from '@common/utils/useInnerWidth';
const SearchContainer = (props) => {
    var _a;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { inputSearch, results, progress, count, showSearch, fromQuery, productSort, firstLoad } = useSelector((state) => state.search);
    const [refineExpanded, setRefineExpanded] = useState(false);
    const isLoading = useSelector((state) => state.loading).requests.includes('SEARCH');
    const { apiErrors } = useSelector((state) => state.errors);
    const currentWidth = useInnerWidth();
    const loadingError = useMemo(() => {
        return apiErrors.find((error) => {
            return error.metadata.location === 'handleSearchFetch';
        });
    }, [apiErrors]);
    const inputRef = useRef();
    const [menuPage, setMenuPage] = useState();
    // per gestire il loading async
    const [localLoading, setLocalLoading] = useState(false);
    const [showNoResults, setShowNoResults] = useState(false);
    const [forceSuggestionsClose, setForceSuggestionClose] = useState(false);
    const searchFormRef = useRef(null);
    const scrollWrapperRef = useRef();
    const [scrollWrapper, setScrollWrapper] = useState();
    useEffect(() => {
        let width = document.querySelector('.search-container');
        if (currentWidth >= 1800) {
            if (width) {
                width.classList.add('search-grid-bigger');
            }
        }
        else {
            if (width) {
                width.classList.remove('search-grid-bigger');
            }
        }
    }, [currentWidth, results]);
    useEffect(() => {
        // console.info('update ref as showSearch updates: ', scrollWrapperRef.current);
        if (showSearch && scrollWrapperRef.current) {
            setScrollWrapper(scrollWrapperRef.current);
        }
    }, [showSearch]);
    // used to set the search box sticky top 0
    const scrolldirection = useScrollDirection({ initialDirection: 'up', thresholdPixels: 100, off: false, scrollContext: scrollWrapper });
    // force suggestion close on scroll down
    useEffect(() => {
        // console.info('scrolldirection: ', scrolldirection);
        if (scrolldirection == 'down') {
            setForceSuggestionClose(true);
        }
        else if (scrolldirection == 'up' && document.activeElement === inputRef.current) {
            setForceSuggestionClose(false);
        }
    }, [scrolldirection]);
    // when redux loading finishes set localLoading to false
    useEffect(() => {
        if (!isLoading) {
            setLocalLoading(false);
        }
    }, [isLoading]);
    const showSuggestions = useMemo(() => inputSearch.length < props.minChars || firstLoad, [results, inputSearch, firstLoad]);
    const toggleShow = (show) => {
        if (show === showSearch)
            return;
        dispatch({
            type: SearchActionTypes.TOGGLE_SEARCH,
            payload: show,
        });
    };
    // TODO da spostare nel saga?
    // open search after fetching data (only from query)
    useEffect(() => {
        if (fromQuery) {
            toggleShow(inputSearch != '');
            if (inputSearch.length >= props.minChars) {
                dispatch({
                    type: SearchActionTypes.FETCH_SEARCH,
                    payload: {
                        resultsType: 'REFINE',
                    },
                });
            }
        }
    }, [inputSearch, fromQuery]);
    useEffect(() => {
        if (showSearch && inputSearch) {
            analytics.searchResults((results === null || results === void 0 ? void 0 : results.length) != 0 ? 'results' : 'no results', inputSearch);
        }
    }, [results]);
    useEffect(() => {
        const minChars = inputSearch.length >= props.minChars;
        if (localLoading) {
            return;
        }
        else {
            setShowNoResults(menuPage && minChars && !results.length);
        }
    }, [menuPage, inputSearch, props.minChars, localLoading, results]);
    // get collections component from menu - Page designer
    useEffect(() => {
        api.getPageJson('menu', true).then((menu) => {
            setMenuPage(menu === null || menu === void 0 ? void 0 : menu.regions);
            delete menu.getFormattedData;
            dispatch({ type: SearchActionTypes.MENU_DATA, payload: menu });
        });
    }, []);
    // used by infinitescroll and load more button
    const fetchProducts = () => {
        dispatch({
            type: SearchActionTypes.FETCH_SEARCH,
            payload: {
                start: progress,
                resultsType: 'PAGE',
            },
        });
    };
    // TODO for clean code should be moved inside saga
    // debounced search from input
    const startSearch = useCallback(debounce(() => {
        dispatch({
            type: SearchActionTypes.FETCH_SEARCH,
            payload: {
                resultsType: 'REFINE',
            },
        });
        dispatch({
            type: SearchActionTypes.QUERY_SEARCH,
        });
    }, props.searchDebounceTimeout || 500), []);
    const updateInputSearch = (search) => {
        dispatch({
            type: SearchActionTypes.INPUT_SEARCH,
            payload: search,
        });
    };
    const hasMore = useMemo(() => {
        return progress < count;
    }, [progress, count]);
    const loader = () => {
        return (!loadingError && (React.createElement("div", { className: "container" },
            React.createElement("span", { className: "plp-loading", "aria-live": "polite" }, t('plp.loading')))));
    };
    const resultLength = () => {
        // Bizarre workaround I found for what appears to be a strange bug of react-infinite-scroll-component
        // Next() function isn't triggered and Loader gets stuck when only 'hasMore' changes but results data stays the same.
        // Specifically this happens in conjunction from what seems to be a cached search result:
        // Sometimes when searching a term, the first page contains products, but the second page has 0 results - that would be a BE / cache issue only
        // But then, if we search another term that would return the same results of the previous, hasMore becomes true but no next() function is triggered when reaching the bottom of the screen ( cfr. OE-22075 )
        // In this way we try to get a not-stale version of the component when this happens.
        return hasMore ? results.length : 0;
    };
    const error = () => {
        if (loadingError && !isLoading) {
            return (React.createElement("div", { className: "container" },
                React.createElement("span", { className: "plp-error", "aria-live": "polite" }, t('plp.error'))));
        }
        return React.createElement(React.Fragment, null);
    };
    const showAccessibleLoadMore = () => {
        if (results.length && hasMore) {
            return (React.createElement("div", { className: "container visible-on-focus" },
                React.createElement("h3", { className: "plp-loading" },
                    " ",
                    t('plp.moreproducts')),
                React.createElement("button", { className: "button button--secondary-inverted", onClick: fetchProducts }, t('plp.loadmore'))));
        }
        return React.createElement(React.Fragment, null);
    };
    const handleCloseSuggestions = (e) => {
        console.info('blur ev: ', e.relatedTarget);
        // !e.relatedTarget ||
        if (!searchFormRef.current.contains(e.relatedTarget)) {
            setForceSuggestionClose(true);
        }
        else {
            setForceSuggestionClose(false);
        }
    };
    const searchField = () => (React.createElement("form", { ref: searchFormRef, role: "search", onFocus: () => setForceSuggestionClose(false), onBlur: handleCloseSuggestions },
        React.createElement("input", { ref: inputRef, type: "search", name: "searchLabel", id: "searchLabel", autoComplete: "off", placeholder: t('search.placeholder'), "aria-label": t('search.placeholder'), "aria-controls": "searchResults", value: inputSearch, onChange: (e) => {
                updateInputSearch(e.currentTarget.value);
                if (e.currentTarget.value != '' && e.currentTarget.value.length >= props.minChars) {
                    startSearch();
                    setLocalLoading(true);
                }
                else {
                    dispatch({
                        type: SearchActionTypes.UPDATE_SEARCH_REFINEMENT,
                        payload: {
                            filterAction: 'RESET',
                        },
                    });
                }
            }, onKeyDown: (e) => {
                if (e.key == 'Enter')
                    e.preventDefault();
            }, onKeyUp: (keyboardEvent) => {
                if (keyboardEvent.code === 'Enter' || keyboardEvent.key === 'Enter') {
                    keyboardEvent.currentTarget.blur();
                }
            } }),
        React.createElement(SearchSuggestionPhrases, { minChars: props.minChars, setLocalLoading: () => setLocalLoading(true), forceClose: forceSuggestionsClose }),
        React.createElement("div", { className: "search-icons" },
            inputSearch != '' && inputSearch.length >= props.minChars && (React.createElement("h2", { className: "search-query" }, localLoading ? t('search.results.loadingm') : count > 0 ? t('search.results.messagem', { count }) : '')),
            inputSearch != '' && inputSearch.length >= props.minChars && (React.createElement("button", { className: "reset-button d-md-none", type: "reset", "aria-label": t('button.cancel'), onClick: () => {
                    analytics.deleteSearch();
                    dispatch({
                        type: SearchActionTypes.RESET_SEARCH,
                    });
                } },
                React.createElement("i", { className: "icon--close-grey" }))),
            React.createElement("button", { className: "icon--close", type: "button", "aria-label": t('button.cancel'), onClick: () => {
                    analytics.closeSearch();
                    toggleShow(false);
                } }))));
    return (React.createElement("div", { className: "search-container" },
        React.createElement("button", { className: "icon--search", type: "button", "aria-label": t('label.search'), "aria-haspopup": "dialog", onClick: () => {
                analytics.header('search');
                toggleShow(true);
                if (scrollWrapper) {
                    scrollWrapper.scrollTo(0, 0);
                }
            } }),
        showSearch && (React.createElement(FocusTrap, { focusTrapOptions: {
                // onDeactivate: () => toggleShow(false),
                allowOutsideClick: true,
                clickOutsideDeactivates: true,
                initialFocus: () => inputRef.current,
            } },
            React.createElement("div", { ref: scrollWrapperRef, id: "infinite-scroll-wrapper", className: classNames('search-overlay', { 'show-results': !showSuggestions }, { 'stick-top': scrolldirection == 'down' || (results.length == 0 && !showSuggestions) }, { 'search-refine-expanded': refineExpanded }) },
                React.createElement("div", { className: classNames('search-input-container') },
                    React.createElement("div", { className: "container" },
                        React.createElement("div", { className: "row" },
                            React.createElement("div", { className: "col-md-6 col-lg-4 search-box" },
                                React.createElement("button", { className: "d-md-none icon--chevron-left-large", type: "button", onClick: () => toggleShow(false) }),
                                searchField()))),
                    !showSuggestions && results.length > 0 && React.createElement(ProductFilters, { setFilterExpandedStatus: (expanded) => setRefineExpanded(expanded), fromSearch: true, loading: localLoading })),
                React.createElement("div", { id: "searchResults", "aria-live": "polite" }, showSuggestions ? (React.createElement(SearchSuggested, { startSearch: () => {
                        startSearch();
                        setLocalLoading(true);
                    }, updateInputSearch: updateInputSearch })) : results.length > 0 ? (React.createElement(React.Fragment, null,
                    React.createElement(InfiniteScroll, { dataLength: resultLength(), next: fetchProducts, hasMore: hasMore, loader: loader(), scrollableTarget: "infinite-scroll-wrapper" },
                        React.createElement("ul", { className: classNames('grid-container', { 'white-bg': window.enableColorBackgroundStillLife }) }, results.map((product, i) => (React.createElement("li", { key: i },
                            React.createElement(ProductTile, { product: product, index: i, type: 'SEARCH', isSimplified: true }),
                            einsteinutils.trackProductInSearch(product.id, inputSearch, productSort === null || productSort === void 0 ? void 0 : productSort.ruleId) && React.createElement(React.Fragment, null))))),
                        showAccessibleLoadMore(),
                        error()))) : (showNoResults && (React.createElement(SearchNoResults, { contactus: props.contactus, component: (_a = menuPage.find((el) => el.id == 'search_region').components) === null || _a === void 0 ? void 0 : _a.find((el) => el.type_id == 'component_search_noresults') })))),
                React.createElement("div", { onClick: () => toggleShow(false), "aria-hidden": "true", className: "search-overlay-close" }))))));
};
export default SearchContainer;
